import React, { useEffect } from "react";
import {
  Cell,
  Column,
  FilterProps,
  Filters,
  Hooks,
  Row,
  SortByFn,
  SortingRule,
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import "./CommonTable.css";
import DefaultColumnFilter from "./DefaultColumnFilter";
import IndeterminateCheckbox from "./IndeterminateCheckbox";
import Pagination from "./Pagination";
import PaginationV2 from "./PaginationV2";
import CommonDropDown from "./DropDown/CommonDropDown";

interface TotalSearchItemProperty {
  show: boolean;
  searchText: string;
}
interface TotalItemProperty {
  show: boolean;
  totalText: string;
}

const SELECTION = "selection";
const BUTTON = "BUTTON";

type CommonTableVariant = "default" | "depth-line" | "no-separator";

type Props<T extends object> = {
  data: Array<T>;
  columns: Array<Column<T>>;
  setSelectedRows?: (rows: Array<T>) => void;
  pageLineSize?: number;
  enablePagination?: boolean;
  enableSelection?: boolean;
  enableMultiSelect?: boolean;
  enableFilter?: boolean;
  enableCheckBoxSelection?: boolean;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  headerTitleStyle?: React.CSSProperties;
  enableManualSorting?: boolean;
  getRowStyle?: (value: T, isSelected: boolean) => React.CSSProperties;
  sortingRules?: SortingRule<T>[];
  sortByFunc?: SortByFn<T>;
  setClearFilter?: (value: { filters: Filters<T>; action: () => void }) => void;
  setFilteredRow?: (rows: Array<T>) => void;
  hidePaginationIfNotNeeded?: boolean;
  variant?: CommonTableVariant;
  toggleSelectAllRow?: boolean; // true = select all, false = deselect all, undefined = normal
  setToggleSelectAllRow?: (select: boolean | undefined) => void; // to set ToggleSelect property above
  initialSelectedRowIndexes?: number[];
  showTotalItem?: TotalItemProperty;
  showTotalSearchProperty?: TotalSearchItemProperty;
};

function getVariantClassName(variant: CommonTableVariant) {
  switch (variant) {
    case "default":
      return "common-table-default";
    case "depth-line":
      return "common-table-depth-line";
    case "no-separator":
      return "common-table-no-separator";
    default:
      return "common-table-default";
  }
}

export default function CommonTable<T extends object>({
  data,
  columns,
  setSelectedRows = () => {},
  enablePagination = true,
  enableSelection = true,
  enableFilter = true,
  enableMultiSelect = true,
  pageLineSize = 12,
  getRowStyle,
  style = {},
  headerStyle = {},
  headerTitleStyle = {},
  sortingRules = [],
  sortByFunc,
  setClearFilter = () => {},
  setFilteredRow = (rows: Array<T>) => {},
  enableManualSorting = true,
  hidePaginationIfNotNeeded = false,
  variant = "default",
  toggleSelectAllRow = undefined,
  setToggleSelectAllRow = () => {},
  initialSelectedRowIndexes = [],
  showTotalItem = {
    show: false,
    totalText: "Totals: ",
  },
  showTotalSearchProperty = {
    show: false,
    searchText: "Search Results",
  },
}: Props<T>) {
  const variantClassName = getVariantClassName(variant);
  const headerCss = variantClassName + "-header";
  const headerTitleCss = headerCss + "-title";
  const headerFilterCss = headerCss + "-filter";
  const rowSelectedCss = variantClassName + "-row-selected";

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );
  let initialSelectedRowIdsRecord: Record<string, boolean> = {} as Record<
    string,
    boolean
  >;
  if (initialSelectedRowIndexes.length > 0) {
    data.forEach((d, indx) => {
      initialSelectedRowIdsRecord[indx.toString()] =
        initialSelectedRowIndexes.includes(indx);
    });
  }

  const selectionHooks = (hooks: Hooks<T>) => {
    hooks.visibleColumns.push((columns) => [
      // Let's make a column for selection
      {
        id: SELECTION,
        // The header can use the table's getToggleAllRowsSelectedProps method
        // to render a checkbox
        Header: <div></div>,
        Filter: ({ getToggleAllRowsSelectedProps }) => (
          <div className="common-table-header-checkbox">
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        defaultCanFilter: true,
        // The cell can use the individual row's getToggleRowSelectedProps method
        // to the render a checkbox
        Cell: ({ row }: Cell<T>) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
        width: "3%",
      },
      ...columns,
    ]);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    toggleAllRowsSelected,
    selectedFlatRows,
    page,
    rows,
    setAllFilters,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize, filters },
  } = useTable(
    {
      columns,
      sortTypes: sortByFunc
        ? {
            alphanumeric: sortByFunc,
          }
        : undefined,
      data,
      defaultColumn,
      initialState: {
        pageSize: pageLineSize,
        sortBy: sortingRules,
        selectedRowIds: initialSelectedRowIdsRecord,
      },
      disableSortBy: !enableManualSorting,
      autoResetSortBy: false,
      autoResetFilters: false,
    },

    enableFilter ? useFilters : () => {},
    useSortBy,
    enablePagination ? usePagination : () => {},
    enableSelection ? useRowSelect : () => {},
    enableSelection && enableMultiSelect ? selectionHooks : () => {}
  );

  useEffect(() => {
    setClearFilter({ filters: filters, action: () => setAllFilters([]) });
  }, [setAllFilters, setClearFilter, filters]);

  useEffect(() => {
    if (enableSelection) {
      setSelectedRows(selectedFlatRows.map((row) => row.original));
    }
  }, [selectedFlatRows, enableSelection]);

  useEffect(() => {
    setFilteredRow(rows.map((row) => row.original));
  }, [rows, setFilteredRow]);

  useEffect(() => {
    if (toggleSelectAllRow !== undefined) {
      toggleAllRowsSelected(toggleSelectAllRow);
      setToggleSelectAllRow(undefined);
    }
  }, [toggleSelectAllRow]);

  const getTableContent = () => {
    const tableBody = enablePagination ? page : rows;

    return tableBody.map((row) => {
      prepareRow(row);
      const style =
        getRowStyle != null ? getRowStyle(row.original, row.isSelected) : {};

      const className = row.isSelected ? rowSelectedCss : "";

      //don't toggle all row if clicked on the selection checkbox
      const onCellClicked = (id: string) => {
        if (id !== SELECTION) {
          toggleAllRowsSelected(false);
          row.toggleRowSelected();
        }
      };

      return (
        <tr {...row.getRowProps()} style={style} className={className}>
          {row.cells.map((cell: any) => {
            const tooltip = cell.column.tooltip
              ? cell.column.tooltip(row.original)
              : null;
            return (
              <td
                {...cell.getCellProps()}
                onClick={() =>
                  enableSelection ? onCellClicked(cell.column.id) : {}
                }
                data-for="common-tooltip"
                data-tip={tooltip}
                data-tip-disable={tooltip == null}
                data-effect="solid"
                data-place="bottom"
                style={getCellStyle(cell.column)}
              >
                {cell.render("Cell")}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  const getColumnStyle = (column: Column<T>): React.CSSProperties => {
    let style: React.CSSProperties = {};
    if (column.width != null) {
      style.width = column.width;
    }

    if (column.align != null) {
      switch (column.align) {
        case "center":
          style.textAlign = "center";
          break;
        case "left":
          style.textAlign = "left";
          break;
        case "right":
          style.textAlign = "right";
          break;
        default:
          break;
      }
    }

    return style;
  };

  const getCellStyle = (column: Column<T>): React.CSSProperties => {
    let style: React.CSSProperties = {};
    if (column.align != null) {
      switch (column.align) {
        case "center":
          style.textAlign = "center";
          break;
        case "left":
          style.textAlign = "left";
          break;
        case "right":
          style.textAlign = "right";
          break;
        default:
          break;
      }
    }

    return style;
  };

  return (
    <div className="common-table-container" style={{ ...style }}>
      <div
        style={{
          gridArea: "table",
        }}
      >
        <table
          {...getTableProps()}
          className={variantClassName}
          cellSpacing="0"
          cellPadding="0"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <th
                    {...column.getHeaderProps()}
                    style={
                      enableMultiSelect && index === 0
                        ? { ...getColumnStyle(column) }
                        : getColumnStyle(column)
                    }
                  >
                    <div className={headerCss} style={headerStyle}>
                      <div
                        className={headerTitleCss}
                        {...column.getSortByToggleProps()}
                        style={headerTitleStyle}
                      >
                        <div>{column.render("Header")}</div>
                        {enableManualSorting && column.isSorted && (
                          <svg
                            width="12"
                            height="6"
                            viewBox="0 0 11 5"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            transform={
                              column.isSortedDesc
                                ? "rotate(0,0,0)"
                                : "rotate(180,0,0)"
                            }
                          >
                            <path
                              d="M5.8772 5L0.877197 0L10.8772 7.29063e-07L5.8772 5Z"
                              fill="#5C5C5C"
                            />
                          </svg>
                        )}
                      </div>
                      <div className={headerFilterCss}>
                        {column.canFilter ? column.render("Filter") : null}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>{getTableContent()}</tbody>
        </table>
      </div>
      {enablePagination && !(hidePaginationIfNotNeeded && pageCount === 1) && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gridArea: "pagination",
            position: "relative",
          }}
        >
          {showTotalItem?.show && (
            <div className="pagination-total-count">
              <label>{showTotalItem.totalText + `${data.length}`}</label>
            </div>
          )}
          {showTotalSearchProperty?.show && (
            <div className="pagination-search-count">
              <div className="pagination-total-count-separator" />
              <label>
                {showTotalSearchProperty.searchText + `${rows.length}`}
              </label>
            </div>
          )}

          {variant === "default" ? (
            <Pagination
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageCount={pageCount}
              gotoPage={gotoPage}
              nextPage={nextPage}
              previousPage={previousPage}
              pageIndex={pageIndex}
            />
          ) : (
            <PaginationV2
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageCount={pageCount}
              gotoPage={gotoPage}
              nextPage={nextPage}
              previousPage={previousPage}
              pageIndex={pageIndex}
              elementPerPage={pageLineSize}
              totalElement={data.length}
            />
          )}
        </div>
      )}
    </div>
  );
}

export type TextColumnFilterProps<T extends object> = {
  filterProps: FilterProps<T>;
  filter: string;
};

export function TextColumnFilter<T extends object>({
  filter,
  filterProps: {
    column: { filterValue = [], render, preFilteredRows, setFilter, id },
  },
}: TextColumnFilterProps<T>) {
  return (
    <input
      id={`${id}_1`}
      value={filterValue[0] || ""}
      type="number"
      onChange={(e) => {
        const val = e.target.value;
        setFilter(val);
      }}
      style={{
        background: "gray",
      }}
    />
  );
}

type SelectionFilterProps = {
  staticFilter?: string[];
  selectId: string;
};

export function SelectColumnFilter<T extends object>({
  column: { filterValue, setFilter, preFilteredRows, id },
  staticFilter,
  selectId,
}: FilterProps<T> & SelectionFilterProps) {
  // Calculate the options for filtering
  // using the preFilteredRows

  const options = React.useMemo(() => {
    const stringOptions = new Set<string>();
    if (staticFilter !== undefined && staticFilter !== null) {
      staticFilter.forEach((row) => {
        stringOptions.add(row);
      });
    } else {
      preFilteredRows.forEach((row) => {
        if (
          row.values[id] !== null &&
          row.values[id] !== undefined &&
          row.values[id] !== ""
        ) {
          stringOptions.add(row.values[id]);
        }
      });
    }

    return Array.from(stringOptions);
  }, [id, preFilteredRows]);

  let dropDownDisplayValues = options.map((option) => ({
    display: option,
    value: option,
    disabled: false,
  }));
  dropDownDisplayValues.unshift({
    display: "All",
    value: "",
    disabled: false,
  });

  // Render a multi-select box
  return (
    <CommonDropDown
      id={selectId}
      value={filterValue || ""}
      onChange={(value) => {
        setFilter(value || undefined);
      }}
      style={{
        width: "100%",
        height: 32,
        fontSize: 13.3,
      }}
      dropDownDisplayValues={dropDownDisplayValues}
    />
  );
}
